import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { examSchema, type ExamFormData, defaultExamValues } from '../examiner/utils/examValidation';
import { CoachExamTabs } from './CoachExamTabs';
import { saveClinicalExam, updateExam, getCustomerExams } from '@/lib/firebase/queries/exams';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Customer, ClinicalExam } from '../examiner/types';
import { loadPerson } from '@/lib/firebase/queries/namatAssessment';
import type { PersonInput } from '@/lib/assessment/namatAssessment';

interface CoachExamDialogProps {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CoachExamDialog({
  customer,
  open,
  onOpenChange,
  onSuccess
}: CoachExamDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [existingExam, setExistingExam] = useState<ClinicalExam | null>(null);
  const [person, setPerson] = useState<PersonInput>({});
  useEffect(() => {
    if (open && customer) loadPerson(customer.id).then(setPerson);
  }, [open, customer]);
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  
  const form = useForm<ExamFormData>({
    resolver: zodResolver(examSchema),
    defaultValues: defaultExamValues
  });

  useEffect(() => {
    const loadLatestExam = async () => {
      if (!customer) return;

      try {
        setIsChecking(true);
        // Get all exams for this customer
        const exams = await getCustomerExams(customer.id);
        
        // Get the latest exam (exams are already sorted by date descending)
        const latestExam = exams.length > 0 ? exams[0] : null;
        
        if (latestExam) {
          console.log('📋 Loading latest exam data for coach:', latestExam);
          setExistingExam(latestExam);
          
          // Convert Firestore timestamps to Date objects for form fields that need dates
          const examDataForForm = {
            ...latestExam,
            date: latestExam.date instanceof Date ? latestExam.date : new Date(latestExam.date),
            createdAt: latestExam.createdAt instanceof Date ? latestExam.createdAt : latestExam.createdAt?.toDate ? latestExam.createdAt.toDate() : new Date(),
            updatedAt: latestExam.updatedAt instanceof Date ? latestExam.updatedAt : latestExam.updatedAt?.toDate ? latestExam.updatedAt.toDate() : new Date()
          };
          
          form.reset(examDataForForm);
          console.log('📝 Form values after reset:', form.getValues());
        } else {
          console.log('🆕 No exam found, using default values');
          setExistingExam(null);
          form.reset(defaultExamValues);
        }
      } catch (error) {
        console.error('Error loading latest exam:', error);
        toast.error('Failed to load examination data');
      } finally {
        setIsChecking(false);
      }
    };

    if (open && customer) {
      loadLatestExam();
    }
  }, [customer, open, form]);

  // Function to clean undefined values from data
  const cleanData = <T,>(obj: T): T => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(cleanData) as T;

    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        cleaned[key] = cleanData(value);
      }
    }
    return cleaned as T;
  };

  const onSubmit = async (data: ExamFormData) => {
    console.log('🚀 Coach exam onSubmit called with data:', data);
    
    if (!customer || !currentUser) {
      toast.error('Missing customer or user information');
      return;
    }

    try {
      setIsLoading(true);
      console.log('💾 Starting coach exam save process...');
      
      // Clean the data to remove undefined values
      const cleanedData = cleanData(data);
      console.log('🧹 Cleaned coach exam data:', cleanedData);
      
      if (existingExam) {
        console.log('📝 Updating existing exam as coach:', existingExam.id);
        const updateData = {
          ...cleanedData,
          status: 'completed',
          updatedAt: new Date(),
          coachId: currentUser.uid, // Add coach ID to track who updated
          updatedByCoach: true // Flag to indicate coach update
        };
        console.log('📊 Coach update data being sent:', updateData);
        await updateExam(existingExam.id, updateData);
        console.log('✅ Exam updated successfully by coach');
        toast.success('Examination updated successfully');
      } else {
        console.log('🆕 Creating new exam as coach');
        const examData = {
          ...cleanedData,
          customerId: customer.id,
          examinerId: currentUser.uid, // Coach acts as examiner
          coachId: currentUser.uid, // Track coach ID
          createdByCoach: true, // Flag to indicate coach creation
          date: new Date(),
          status: 'completed' as const
        };
        console.log('📋 Coach exam data to save:', examData);
        const savedExamId = await saveClinicalExam(examData);
        console.log('✅ Exam saved successfully by coach with ID:', savedExamId);
        toast.success('Examination completed successfully');
      }

      console.log('🔄 Resetting form and closing dialog');
      form.reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('❌ Error saving coach exam:', error);
      toast.error('Failed to save examination');
    } finally {
      setIsLoading(false);
      console.log('🏁 Coach save process completed');
    }
  };

  if (isChecking) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.coach.examination.title.loading')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[65%] w-[65%]">
        <DialogHeader>
          <DialogTitle>
            {existingExam
              ? t('dashboard.coach.examination.title.edit')
              : t('dashboard.coach.examination.title.record')}
            {customer && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                - {customer.firstNameEn} {customer.lastNameEn}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.log('❌ Coach form validation errors:', errors);
            console.log('📝 Current coach form values:', form.getValues());
            
            // Log specific error details
            Object.keys(errors).forEach(key => {
              console.log(`🔴 Error in ${key}:`, errors[key as keyof typeof errors]);
            });
          })} className="space-y-4">
            <CoachExamTabs form={form} person={person} />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  existingExam ? t('common.update') : t('common.complete')
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
